import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ComparativeAnalysisProps {
  reportId: string;
  clientLoss: number;
  eventType: string;
}

interface Comparison {
  industry_avg: number;
  similar_claims_avg: number;
  percentile: number;
  variance_from_avg_pct: number;
}

interface Insight {
  type: 'positive' | 'neutral' | 'negative';
  message: string;
  icon: string;
}

export default function ComparativeAnalysis({ reportId, clientLoss, eventType }: ComparativeAnalysisProps) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<any>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, [reportId]);

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/benchmarks/analysis/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAnalysis(response.data.analysis);
      setLoading(false);
    } catch (err: any) {
      console.error('Comparative analysis error:', err);
      setError(err.response?.data?.detail || 'Failed to load comparative analysis');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-md p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Comparative Analysis</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
          <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading industry benchmarks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-md p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Comparative Analysis</h3>
          <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>PREVIEW</span>
        </div>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Comparative analysis will show how your claim compares to similar {eventType} claims
          and industry benchmarks. Currently in development.
        </p>
      </div>
    );
  }

  if (!analysis) return null;

  const { comparisons, percentile, insights } = analysis;

  return (
    <div className="border rounded-md p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Comparative Analysis</h3>
          <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>INDUSTRY BENCHMARKS</span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm font-semibold"
          style={{ color: 'var(--color-needs-human-text)' }}
        >
          {expanded ? '▼ Hide Details' : '▶ Show Details'}
        </button>
      </div>

      {/* Quick Summary */}
      <div className="rounded-md p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Your Loss */}
          <div>
            <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Your Calculated Loss:</p>
            <p className="text-2xl font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={clientLoss} /></p>
          </div>

          {/* Industry Average */}
          <div>
            <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Industry Average ({eventType}):</p>
            <p className="text-2xl font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-needs-human-text)' }}>
              <FinancialValue value={comparisons?.industry_avg} />
            </p>
          </div>

          {/* Percentile */}
          <div>
            <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Your Percentile Rank:</p>
            <p className="text-2xl font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-needs-human-text)' }}>
              {percentile?.value ? `${percentile.value}th` : 'N/A'}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {percentile?.value && percentile.value > 75 && 'Higher than 75% of claims'}
              {percentile?.value && percentile.value <= 75 && percentile.value > 50 && 'Above median'}
              {percentile?.value && percentile.value <= 50 && 'Below median'}
            </p>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          Key Insights
        </h4>
        <div className="space-y-2">
          {insights?.map((insight: Insight, idx: number) => (
            <div
              key={idx}
              className="p-3 rounded-md border"
              style={
                insight.type === 'positive' ? { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' } :
                insight.type === 'negative' ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' } :
                { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }
              }
            >
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <span className="mr-2">{insight.icon}</span>
                {insight.message}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && comparisons && (
        <div className="mt-4 rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <h4 className="font-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>Detailed Comparison</h4>

          <div className="space-y-3">
            {/* Similar Claims Comparison */}
            <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Similar Claims Average:</p>
              <p className="text-lg font-bold font-mono tabular-nums text-right" style={{ color: 'var(--color-text-primary)' }}>
                <FinancialValue value={comparisons.similar_claims_avg} />
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Based on {comparisons.sample_size || 0} similar {eventType} claims
              </p>
            </div>

            {/* Variance from Average */}
            <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Variance from Industry Average:</p>
              <p className="text-lg font-bold font-mono tabular-nums text-right" style={{
                color: comparisons.variance_from_avg_pct > 0 ? 'var(--color-needs-human-text)' : 'var(--color-verified)'
              }}>
                {comparisons.variance_from_avg_pct > 0 ? '+' : ''}
                {comparisons.variance_from_avg_pct?.toFixed(1)}%
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                {comparisons.variance_from_avg_pct > 0 ? 'Above' : 'Below'} industry average
              </p>
            </div>

            {/* Context */}
            <div className="p-3 rounded-md border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                <strong>Note:</strong> Comparative analysis provides context for your claim.
                Higher percentiles don't necessarily indicate issues - they may reflect
                unique circumstances, larger business size, or more severe event impact.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
